/**
 * Outbound link helpers.
 *
 * The marketing site (trainzilla.app) and the coach app (app.trainzilla.in) share ONE GA4
 * property (G-9Q8E40LN2K) with the cross-domain linker on both sides, so a visitor's session
 * (and its real source: Google, an ad, a QR code) carries into the app and its sign_up event.
 * UTMs on those links would START A NEW SESSION credited to "trainzilla.in / referral" and wipe
 * the real source, so app.trainzilla.in links are left untagged; the GA4 `cta_click` event on
 * this side records which button was used. Store links still get UTMs (separate analytics).
 */
import { trackCtaClick } from '../services/analytics';

const STORE_HOSTS = ['play.google.com', 'apps.apple.com'];

/** Append UTM params to an outbound URL (only for known destinations, idempotent). */
export function withUtm(
  url: string,
  { campaign, medium = 'referral', content }: { campaign: string; medium?: string; content?: string },
): string {
  try {
    const u = new URL(url);
    if (!STORE_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`))) return url;
    if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'trainzilla.app');
    if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', medium);
    if (!u.searchParams.has('utm_campaign')) u.searchParams.set('utm_campaign', campaign);
    if (content && !u.searchParams.has('utm_content')) u.searchParams.set('utm_content', content);
    return u.toString();
  } catch {
    return url;
  }
}

/** onClick handler that records a GA4 cta_click before the browser follows the link. */
export function trackedOutboundClick(label: string, extra?: Record<string, any>) {
  return () => trackCtaClick(label, { outbound: true, ...extra });
}
